import { useEffect, useState } from 'react';
import { Bot } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrgStore } from '@/stores/org-store';
import { listChatAgents } from '@/services/chat-agents/agents';
import { ChatAgentChatPage } from '@/pages/chat-agents/chat-agent-chat-page';
import { WorkflowBuilderHeading } from './workflow-builder-heading';

export function WorkflowBuilderPage() {
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const [resolvedAgentId, setResolvedAgentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [noAgent, setNoAgent] = useState(false);

  useEffect(() => {
    const resolve = async () => {
      if (!currentOrg?.orgid) return;

      // Reset state before fetching so stale values don't persist across org switches
      setResolvedAgentId(null);
      setNoAgent(false);
      setIsLoading(true);

      try {
        const agents = await listChatAgents(currentOrg.orgid);
        if (agents.length === 0) {
          setNoAgent(true);
          return;
        }

        // Select deterministically to avoid relying on backend ordering
        const sortedAgents = [...agents].sort((a, b) =>
          (a.id || '').localeCompare(b.id || ''),
        );
        if (sortedAgents.length > 1) {
          Sentry.captureMessage(
            'Multiple workflow agents found for org; using first by sorted id',
            { level: 'warning' },
          );
        }
        setResolvedAgentId(sortedAgents[0].id);
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'workflow-builder-resolve-agent' },
        });
        setNoAgent(true);
      } finally {
        setIsLoading(false);
      }
    };
    resolve();
  }, [currentOrg?.orgid]);

  if (isLoading) {
    return (
      <div className='flex h-full'>
        {/* Sidebar skeleton */}
        <div className='w-[252px] shrink-0 border-r bg-sidebar flex flex-col'>
          <div className='flex items-center gap-2 p-3'>
            <Skeleton className='size-6 rounded' />
            <Skeleton className='h-4 w-24' />
          </div>
          <div className='flex flex-col gap-2 px-3'>
            <Skeleton className='h-9 w-full rounded-lg' />
            <Skeleton className='h-8 w-full rounded-md' />
            <Skeleton className='h-8 w-3/4 rounded-md' />
            <Skeleton className='h-8 w-5/6 rounded-md' />
          </div>
        </div>

        {/* Main area skeleton */}
        <div className='flex flex-1 flex-col min-w-0 items-center justify-center gap-4 px-4 pb-6'>
          <div className='w-full max-w-3xl px-[20px] flex flex-col gap-4'>
            {/* Heading skeleton */}
            <div className='flex flex-col gap-2'>
              <div className='flex items-center gap-2'>
                <Skeleton className='size-6 rounded' />
                <Skeleton className='h-5 w-40' />
              </div>
              <Skeleton className='h-8 w-80' />
            </div>
            {/* Input skeleton */}
            <Skeleton className='h-11 w-full rounded-3xl' />
          </div>
        </div>
      </div>
    );
  }

  if (noAgent) {
    return (
      <div className='flex flex-col items-center justify-center h-full text-center px-4 gap-3'>
        <div className='flex size-12 items-center justify-center rounded-lg bg-muted'>
          <Bot className='size-6 text-muted-foreground' />
        </div>
        <h2 className='text-xl font-bold'>Workflow Builder</h2>
        <p className='text-muted-foreground text-sm max-w-md'>
          The Workflow Builder agent hasn&apos;t been configured yet. Please
          contact your administrator to set it up.
        </p>
      </div>
    );
  }

  return (
    <ChatAgentChatPage
      agentIdOverride={resolvedAgentId!}
      showSettings={false}
      showInlineHistory
      startFresh
      sidebarTitle='Builder Agent'
      emptyStateHeading={<WorkflowBuilderHeading />}
    />
  );
}
